import React, { useEffect, useState } from 'react';
import { FormControl, FormHelperText, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import AdminState from '../AdminState';
import SubmitButton from './SubmitButton';
import './NewMachineDropDown.css';

export default function SliderFormDropDown({ title, fields, dataFunctionMachineTypes, dataFunctionToolCategories, onSubmit }) {
  const [submission, setSubmission] = useState(() => ({
    ...Object.fromEntries(fields.map(field => [field.key, field.defaultValue ?? ''])),
    machineType: '', categoryID: '',
  }));
  const [tools, setTools] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  const [errors, setErrors] = useState({});
  const [pending, setPending] = useState(false);
  const [saveError, setSaveError] = useState(false);
  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError(false);
    Promise.all([dataFunctionMachineTypes(), dataFunctionToolCategories()])
      .then(([toolResponse, categoryResponse]) => {
        if (toolResponse.status !== 200 || categoryResponse.status !== 200) throw new Error('load_failed');
        if (!active) return;
        setTools(toolResponse.items || []);
        setCategories(categoryResponse.items || []);
      }).catch(() => { if (active) setLoadError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [dataFunctionMachineTypes, dataFunctionToolCategories, retry]);
  const change = (key, value) => {
    setSubmission(current => ({ ...current, [key]: value }));
    setErrors(current => ({ ...current, [key]: '' }));
  };
  const submit = async event => {
    event.preventDefault();
    if (pending) return;
    const nextErrors = {};
    fields.forEach(field => {
      const value = String(submission[field.key] ?? '');
      if (!value.trim()) nextErrors[field.key] = `${field.placeholder} is required.`;
      else if (field.validate && !value.match(field.validate)) nextErrors[field.key] = field.errorText;
    });
    if (!tools.some(tool => tool.name === submission.machineType)) nextErrors.machineType = 'Choose a tool.';
    if (!categories.some(category => String(category.categoryID) === String(submission.categoryID))) nextErrors.categoryID = 'Choose a tool category.';
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      event.currentTarget.querySelector(`[id="${Object.keys(nextErrors)[0]}"]`)?.focus();
      return;
    }
    setPending(true);
    setSaveError(false);
    try { await onSubmit(submission); }
    catch { setSaveError(true); }
    finally { setPending(false); }
  };
  if (loadError) return <AdminState status="error" title="Tool choices could not be loaded"
    description="Try again to load the tools and categories for this space." onRetry={() => setRetry(value => value + 1)} />;
  if (loading) return <AdminState status="loading" title="Loading tools and categories…" />;
  const missingChoices = !tools.length || !categories.length;
  return <form className="SliderFormDropDown" onSubmit={submit} noValidate>
    {saveError && <AdminState status="error" title="Tool instance could not be created"
      description="Your entries are still here. Try creating the tool instance again." mb={0} />}
    {missingChoices && <AdminState title="Tools and categories are required"
      description="Add at least one tool and one tool category before creating a tool instance." mb={0} />}
    {fields.map(field => <TextField fullWidth required key={field.key} id={field.key} name={field.key}
      label={field.placeholder} helperText={errors[field.key]} error={Boolean(errors[field.key])}
      value={submission[field.key]} onChange={event => change(field.key, event.target.value)} disabled={field.disabled || pending} />)}
    <FormControl fullWidth required error={Boolean(errors.machineType)} disabled={pending || !tools.length}>
      <InputLabel id="machineType-label" htmlFor="machineType">{title}</InputLabel>
      <Select labelId="machineType-label" inputProps={{ id: 'machineType', 'aria-describedby': errors.machineType ? 'machineType-error' : undefined }}
        value={submission.machineType} onChange={event => change('machineType', event.target.value)}>
        {tools.map(tool => <MenuItem key={tool.id || tool.name} value={tool.name}>{tool.name}</MenuItem>)}
      </Select>
      {errors.machineType && <FormHelperText id="machineType-error">{errors.machineType}</FormHelperText>}
    </FormControl>
    <FormControl fullWidth required error={Boolean(errors.categoryID)} disabled={pending || !categories.length}>
      <InputLabel id="categoryID-label" htmlFor="categoryID">Tool category</InputLabel>
      <Select labelId="categoryID-label" inputProps={{ id: 'categoryID', 'aria-describedby': errors.categoryID ? 'categoryID-error' : undefined }}
        value={submission.categoryID} onChange={event => change('categoryID', event.target.value)}>
        {categories.map(category => <MenuItem key={category.categoryID} value={category.categoryID}>{category.categoryName}</MenuItem>)}
      </Select>
      {errors.categoryID && <FormHelperText id="categoryID-error">{errors.categoryID}</FormHelperText>}
    </FormControl>
    <div className="admin-form-actions admin-form-actions-end"><SubmitButton pending={pending} disabled={missingChoices} /></div>
  </form>;
}
